import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useSemesters } from './semester-api'
import type { Semester } from './semester-types'

const SLEUTEL = 'inzetplanning.semester'

interface SemesterKeuze {
  semesterId: string
  semester: Semester | null
  semesters: Semester[]
  isLoading: boolean
  isError: boolean
  kies: (semesterId: string) => void
}

const SemesterContext = createContext<SemesterKeuze | null>(null)

function leesOpgeslagen(): string {
  try {
    return localStorage.getItem(SLEUTEL) ?? ''
  } catch {
    return ''
  }
}

function bewaar(semesterId: string): void {
  try {
    localStorage.setItem(SLEUTEL, semesterId)
  } catch {
    return
  }
}

export function SemesterProvider({ children }: { children: ReactNode }) {
  const query = useSemesters()
  const [gekozen, setGekozen] = useState(leesOpgeslagen)
  const semesters = query.data
  const isLoading = query.isLoading
  const isError = query.isError

  const kies = useCallback((semesterId: string) => {
    setGekozen(semesterId)
    bewaar(semesterId)
  }, [])

  const waarde = useMemo<SemesterKeuze>(() => {
    const lijst = semesters ?? []
    const standaard = lijst.find((item) => item.status === 'actief') ?? lijst[0]
    const geldig = lijst.some((item) => item.id === gekozen) ? gekozen : ''
    const semesterId = geldig || standaard?.id || ''
    return {
      semesterId,
      semester: lijst.find((item) => item.id === semesterId) ?? null,
      semesters: lijst,
      isLoading,
      isError,
      kies,
    }
  }, [semesters, gekozen, isLoading, isError, kies])

  return <SemesterContext.Provider value={waarde}>{children}</SemesterContext.Provider>
}

export function useSemesterKeuze(): SemesterKeuze {
  const waarde = useContext(SemesterContext)
  if (!waarde) throw new Error('SemesterProvider ontbreekt boven deze component')
  return waarde
}
